/*
  companio-modal.js — the homepage subscribe modal. Window ③, 2026-08-28.

  WHY A FOURTH SCRIPT AND NOT A data- HOOK ON AN EXISTING ONE.
  companio-rail.js is horizontal scrolling, companio-ugc.js is video
  throttling, companio-companions.js is a snap carousel. None of them do focus
  management or storage-backed frequency capping, which is all this file is.
  It shares nothing with them, so the "no second implementation" rule in §4.5
  is not in play. If a second modal is ever needed, give it [data-subscribe]
  semantics and generalise THIS file rather than writing a fifth.

  THE TRIGGER IS TWO CONDITIONS, BOTH REQUIRED: scrolled past the threshold
  AND dwelled long enough. Not either/or. A pure timer fires at someone who
  has seen nothing yet and has not decided whether they care; a pure scroll
  fires at someone who is still moving. Together they mean "stopped, on
  something".

  THE 15% DEFAULT IS "JUST PAST THE HERO", MEASURED. The homepage artboard is
  5994 tall and the hero ends at 801 — 13.4%. So the percentage and the
  "past the hero" rule in the spec are the same line on this page, and a
  percentage is one predictable knob a merchant can actually reason about
  instead of a selector that breaks when a section is renamed.

  EXIT INTENT IS GATED ON DWELL. The spec allows exit intent as a desktop
  fallback. Left ungated it fires at someone who arrived 900ms ago and bounced,
  which is the exact behaviour the two-condition rule exists to prevent. It
  needs the dwell timer, but not the scroll.

  MOBILE HAS NO EXIT INTENT AND NO AUTOFOCUS. There is no mouseout-to-leave
  event on a touch device, and focusing the email field raises the keyboard
  over half the dialog. On coarse pointers the panel itself takes focus.

  STORAGE CAN THROW, NOT JUST RETURN NULL. Private windows and blocked site
  data make localStorage.getItem raise rather than return null, so every read
  and write goes through get()/set(). When storage is unavailable the modal
  degrades to once per page load, which is the safe direction: annoying at
  worst, never a hard error.

  SUCCESS COMES BACK THROUGH THE URL, ON PURPOSE.
  Shopify's {% form 'customer' %} reloads the page, and form.posted_successfully?
  is true for EVERY customer form on the page — including the header's
  coming-soon capture, which is also a customer form. Keying off it would pop
  this modal saying "you're on the list" at someone who just asked to be told
  about T-shirts. So this form carries its own return_to marker
  (?companio_sub=1) and only that marker opens the success state. The marker
  is stripped from the URL afterwards so a reload or a shared link does not
  replay it.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-subscribe]');
  if (!root) return;

  var panel = root.querySelector('[data-subscribe-panel]');
  var closeBtn = root.querySelector('[data-subscribe-close]');
  var emailInput = root.querySelector('[data-subscribe-email]');
  var successEl = root.querySelector('[data-subscribe-success]');
  var formWrap = root.querySelector('[data-subscribe-form]');
  if (!panel) return;

  var SEEN = 'companio:sub:seen';
  var DISMISSED = 'companio:sub:dismissed';
  var DONE = 'companio:sub:done';
  var UTM = 'companio:sub:utm';

  function get(store, key) {
    try { return window[store].getItem(key); } catch (e) { return null; }
  }

  function set(store, key, value) {
    try { window[store].setItem(key, value); } catch (e) { /* private mode */ }
  }

  var dwellMs = (parseFloat(root.getAttribute('data-dwell')) || 5) * 1000;
  var scrollPct = (parseFloat(root.getAttribute('data-scroll')) || 15) / 100;
  var cooldownMs = (parseFloat(root.getAttribute('data-cooldown')) || 7) * 86400000;
  var isCustomer = root.getAttribute('data-customer') === 'true';
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  var opened = false;
  var dwellMet = false;
  var scrollMet = false;
  var lastFocus = null;

  try {
    if (/(^|[?&])utm_medium=email(&|$)/i.test(window.location.search)) {
      set('sessionStorage', UTM, '1');
    }
  } catch (e) { /* no-op */ }

  function suppressed() {
    if (isCustomer) return true;
    if (get('sessionStorage', UTM) === '1') return true;
    if (get('localStorage', DONE) === '1') return true;
    if (get('sessionStorage', SEEN) === '1') return true;
    var stamp = parseInt(get('localStorage', DISMISSED) || '0', 10);
    if (stamp && (Date.now() - stamp) < cooldownMs) return true;
    return false;
  }

  function focusables() {
    var sel = 'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
    return Array.prototype.filter.call(panel.querySelectorAll(sel), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0;
    });
  }

  function onKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      close(true);
      return;
    }
    if (e.key !== 'Tab') return;

    var list = focusables();
    if (!list.length) {
      e.preventDefault();
      panel.focus();
      return;
    }
    var first = list[0];
    var last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function open(asSuccess) {
    if (opened) return;
    opened = true;
    lastFocus = document.activeElement;
    set('sessionStorage', SEEN, '1');

    if (asSuccess) {
      if (successEl) successEl.hidden = false;
      if (formWrap) formWrap.hidden = true;
    }

    root.hidden = false;
    window.requestAnimationFrame(function () {
      root.classList.add('is-open');
    });
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeydown);

    if (finePointer && emailInput && !asSuccess) {
      emailInput.focus();
    } else {
      panel.focus();
    }
  }

  function close(remember) {
    if (!opened) return;
    opened = false;
    root.classList.remove('is-open');
    root.hidden = true;
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', onKeydown);
    if (remember) set('localStorage', DISMISSED, String(Date.now()));
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }

  function maybeOpen() {
    if (opened || !dwellMet || !scrollMet) return;
    if (suppressed()) return;
    open(false);
  }

  /* Wire the controls BEFORE any early return, or a modal opened by the
     success path would have a dead close button. */
  if (closeBtn) {
    closeBtn.addEventListener('click', function () { close(true); });
  }
  root.addEventListener('click', function (e) {
    if (e.target === root) close(true);
  });

  /* Came back from a successful submit on THIS form. */
  if (/(^|[?&])companio_sub=1(&|$)/.test(window.location.search)) {
    set('localStorage', DONE, '1');
    open(true);

    if (window.history && window.history.replaceState) {
      var qs = window.location.search
        .replace(/(^|[?&])companio_sub=1(&|$)/, '$1')
        .replace(/^[?&]+/, '')
        .replace(/[?&]+$/, '');
      var clean = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
      window.history.replaceState({}, '', clean);
    }
    return;
  }

  if (suppressed()) return;

  window.setTimeout(function () {
    dwellMet = true;
    maybeOpen();
  }, dwellMs);

  function onScroll() {
    var doc = document.documentElement;
    var max = doc.scrollHeight - window.innerHeight;
    if (max <= 0) {
      scrollMet = true;
    } else {
      var target = Math.min(doc.scrollHeight * scrollPct, max);
      var y = window.scrollY || doc.scrollTop || 0;
      if (y >= target) scrollMet = true;
    }
    if (scrollMet) {
      window.removeEventListener('scroll', onScroll);
      maybeOpen();
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true });

  if (finePointer) {
    document.addEventListener('mouseout', function (e) {
      if (e.relatedTarget || e.clientY > 0) return;
      if (opened || !dwellMet || suppressed()) return;
      open(false);
    });
  }
})();
